import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Button, IconButton, MediaWidth, MixerLoader, Modal, YesNo } from '../../components/Common';
import { RootState } from '../../store';
import {
    useHttpDeleteLeaveRequest,
    useHttpGetHolidaysByBranchOnMount,
    useHttpGetLeaveRequestsOnMountAndExecute,
    useHttpGetMedicalLeaveRequestsForUserOnMount,
    useUserOrganizationPolicies,
    useHttpDeleteMedicalLeaveRequest,
    useOrganizationId,
} from '../../hooks';
import { LeaveRequestDto, LeaveRequestStatus, LeaveRequestType } from '../../commonTypes/leaveRequest.types';
import { MedicalLeaveRequestBaseResponseDto, PolicyDto } from '../../commonTypes/user.types';
import { dateTimeComparator } from '../../util/date/dateHelper';
import DiagramSlider, { DiagramSlide } from '../../components/DiagramSlider';
import { Diagram } from '../../components/Diagram';
import RequestedDays from '../../components/RequestedDays';
import cc from 'classcat';
import { MessageBox } from '../../components/Common';
import RequestLeave from '../../components/modalContent/RequestLeave';
import RequestMedicalLeave from '../../components/modalContent/RequestLeave/RequestMedicalLeave';
import useHttpGetUserPoliciesOMX from '../../hooks/acs/acsApi/useHttpGetUserPoliciesOMX';
import { AuthState } from '../../store/slices/auth.slice';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../hooks/useTranslation';
import { LeaveRequestUserInfoIncluded } from '../../commonTypes/acs';
import { LeaveDurationTypeEnum } from '@cyberlabrs/helpers';
import useHttpGetMedicalLeaveByOrganizationOnMount from '../../hooks/organization/organizationApi/useHttpGetMedicalLeaveByOrganizationOnMount';

const userHasPolicies = (policies?: PolicyDto[]) => {
    return policies && policies.length > 0;
};

const LeaveRequest = () => {
    const { t } = useTranslation();
    const policies = useUserOrganizationPolicies();
    const organizationId = useOrganizationId();
    const { medicalLeave } = useHttpGetMedicalLeaveByOrganizationOnMount(organizationId);

    const [policyDescription, setPolicyDescription] = useState('');
    const refreshPolicies = useHttpGetUserPoliciesOMX();
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const { data: holidays } = useHttpGetHolidaysByBranchOnMount(auth.value.branchId);

    const leaveRequestToDeleteId = useRef<string>('');
    const leaveRequestToDeleteType = useRef<LeaveRequestType | null>(null);
    const [isDeleteDORModalVisible, setDeleteDORModalVisible] = useState(false);
    const { deleteLeaveRequest } = useHttpDeleteLeaveRequest();
    const { deleteMedicalLeaveRequest } = useHttpDeleteMedicalLeaveRequest();

    const [isRejectDetailsModalVisible, setRejectDetailsModalVisibility] = useState(false);
    const [rejectReason, setRejectReason] = useState<string | undefined>();

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(6);

    const [pageMedical, setPageMedical] = useState(0);
    const [sizeMedical, setSizeMedical] = useState(2);

    const [viewAllRequests, setViewAllRequests] = useState(false);
    const [viewAllMedicalRequests, setViewAllMedicalRequests] = useState(false);
    const [isRequestModalVisible, setRequestModalVisibility] = useState(false);
    const [isMedicalRequestModalVisible, setMedicalRequestModalVisibility] = useState(false);

    const {
        exec: execAllRequests,
        abort: abortAllRequests,
        data: allRequests,
        setData: setAllRequests,
        total,
        setTotal,
        loading: loadingLeaveRequests,
    } = useHttpGetLeaveRequestsOnMountAndExecute(page, size, 'fromDate', 'desc');

    const {
        exec: execMedicalRequests,
        abort: abortMedicalRequests,
        data: medicalRequests,
        setData: setMedicalRequests,
        total: medicalTotal,
        setTotal: setMedicalTotal,
        loading: loadingMedicalRequests,
    } = useHttpGetMedicalLeaveRequestsForUserOnMount(pageMedical, sizeMedical, 'startDate', 'desc');

    const mapMedicalToLeaveRequest = (medical: MedicalLeaveRequestBaseResponseDto): LeaveRequestUserInfoIncluded => ({
        id: medical.id,
        userSub: medical.userSub,
        description: medical.description ?? undefined,
        status: medical.status,
        createdAt: new Date(medical.createdAt).toISOString(),
        fromDate: new Date(medical.startDate).toISOString(),
        toDate: medical.endDate ? new Date(medical.endDate).toISOString() : 'Present',
        policyType: LeaveRequestType.MEDICAL_LEAVE,
        leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
        email: '',
        rejectReason: medical.rejectReason,
    });

    const medicalLeaveRequests = medicalRequests.map(req => mapMedicalToLeaveRequest(req));

    const [isTermsOpen, setIsTermsOpen] = useState(false);

    useEffect(() => {
        execAllRequests();
        execMedicalRequests();
        return () => {
            abortAllRequests();
            abortMedicalRequests();
        };
    }, [page, size, pageMedical, sizeMedical]);

    const openDeleteModal = (id: string, requestType?: LeaveRequestType) => {
        leaveRequestToDeleteId.current = id;
        leaveRequestToDeleteType.current = requestType || null;
        setDeleteDORModalVisible(true);
    };

    useEffect(() => {
        viewAllRequests ? setSize(10) : setSize(6);
        viewAllMedicalRequests ? setSizeMedical(10) : setSizeMedical(2);
    }, [viewAllRequests, viewAllMedicalRequests]);

    const removeLeaveRequestFromList = () => {
        refreshPolicies();
        execAllRequests();
        execMedicalRequests();
    };

    const addNewRequests = (newRequests: LeaveRequestDto[]) => {
        const updatedRequests = [...allRequests, ...newRequests];
        updatedRequests.sort((reqA, reqB) => dateTimeComparator(reqA.createdAt, reqB.createdAt));
        const trimmedRequests = updatedRequests.slice(0, size);
        setAllRequests(trimmedRequests);
        setTotal(prevTotal => prevTotal + newRequests.length);
        refreshPolicies();
    };

    const editOngoingLeaveRequestCb = (updatedRequest: LeaveRequestDto) => {
        const { toDate } = updatedRequest;
        setAllRequests(allRequests.map(req => (req.id === updatedRequest.id ? { ...req, toDate } : { ...req })));
        const oldRequest = allRequests.find(req => req.id === updatedRequest.id);
        if (!oldRequest) return;
        execAllRequests();
        refreshPolicies();
    };

    const openRejectDetailsModal = (reason?: string) => {
        setRejectReason(reason);
        setRejectDetailsModalVisibility(true);
    };

    const deleteLeaveRequestCb = (requestId: string) => {
        const isMedicalLeave = leaveRequestToDeleteType.current === LeaveRequestType.MEDICAL_LEAVE;

        if (isMedicalLeave) {
            deleteMedicalLeaveRequest(requestId, { onSuccess: removeLeaveRequestFromList });
        } else {
            deleteLeaveRequest(requestId, { onSuccess: removeLeaveRequestFromList });
        }
    };

    const createLeaveRequestOnSuccess = (requests: LeaveRequestDto[]) => {
        const leaveRquestsToAdd: LeaveRequestDto[] = [];
        for (const request of requests) {
            execAllRequests();
            const policy = policies.find(policy => policy.leaveType === request.policyType);
            if (policy?.autoApproved) request.status = LeaveRequestStatus.APPROVED;
            request.createdAt = DateTime.now().toISO();

            leaveRquestsToAdd.push(request);
        }
        addNewRequests(leaveRquestsToAdd);
    };

    const createMedicalLeaveRequestOnSuccess = (request: MedicalLeaveRequestBaseResponseDto) => {
        const updatedRequests = [...medicalRequests, request];
        updatedRequests.sort((reqA, reqB) => dateTimeComparator(reqA.createdAt, reqB.createdAt));
        const trimmedRequests = updatedRequests.slice(0, sizeMedical);
        setMedicalRequests(trimmedRequests);
        setMedicalTotal(prevTotal => prevTotal + 1);
    };

    const showDescriptionModal = (description: string) => {
        if (!description) setPolicyDescription('Policy description is not provided');
        else setPolicyDescription(description);
        setIsTermsOpen(true);
    };

    const handleRequestLeaveClick = () => {
        setRequestModalVisibility(true);
    };

    return (
        <main className="leave-request">
            {loadingLeaveRequests || loadingMedicalRequests ? (
                <MixerLoader position="centerOnParent" />
            ) : (
                <div className={cc(['leave-request__components', { 'leave-request__components--all': viewAllRequests }])}>
                    {!viewAllRequests && !viewAllMedicalRequests && (
                        <DiagramSlider>
                            {policies
                                .filter(policy => policy.isActive)
                                .map(policy => (
                                    <DiagramSlide key={policy.id}>
                                        <Diagram
                                            policy={policy}
                                            annualColor="#4B99F5"
                                            usedColor="#05254B"
                                            carriedOverColor="#7CD992"
                                            setIsTermsOpen={() => showDescriptionModal(policy.description ?? '')}
                                        />
                                    </DiagramSlide>
                                ))}
                        </DiagramSlider>
                    )}
                    <div className="leave-request__button-wrapper">
                        <MediaWidth max="lg">
                            <IconButton iconUrl="/svg/plus.svg" iconAlt="Plus" onClick={handleRequestLeaveClick} />
                        </MediaWidth>
                        <MediaWidth min="lg">
                            <Button text="Request Leave" onClick={handleRequestLeaveClick} />
                        </MediaWidth>
                    </div>
                    <RequestedDays
                        requests={allRequests.map(request => {
                            return { email: '', ...request };
                        })}
                        viewAll={viewAllRequests}
                        setViewAll={setViewAllRequests}
                        paginationProps={{ page: page, total, elementsPerPage: size, setPage, setElementsPerPage: setSize }}
                        openRejectDetailsModal={openRejectDetailsModal}
                        openDeleteModal={openDeleteModal}
                        editOngoingLeaveRequestCb={editOngoingLeaveRequestCb}
                        holidays={holidays}
                        title={t('leaveRequest.requestedDays')}
                    />
                    <div className="leave-request__medical-leave">
                        <RequestedDays
                            requests={medicalLeaveRequests}
                            viewAll={viewAllMedicalRequests}
                            setViewAll={setViewAllMedicalRequests}
                            paginationProps={{
                                page: pageMedical,
                                total: medicalTotal,
                                elementsPerPage: sizeMedical,
                                setPage: setPageMedical,
                                setElementsPerPage: setSizeMedical,
                            }}
                            openRejectDetailsModal={openRejectDetailsModal}
                            openDeleteModal={openDeleteModal}
                            editOngoingLeaveRequestCb={editOngoingLeaveRequestCb}
                            holidays={holidays}
                            title={t('medicalLeaveRequest.medicalLeaveRequests')}
                        />
                    </div>
                </div>
            )}
            {userHasPolicies(policies) && (
                <Modal isVisible={isRequestModalVisible} closeModal={() => setRequestModalVisibility(false)}>
                    <RequestLeave close={() => setRequestModalVisibility(false)} createLeaveRequestOnSuccess={createLeaveRequestOnSuccess} />
                </Modal>
            )}

            {medicalLeave && (
                <Modal isVisible={isMedicalRequestModalVisible} closeModal={() => setMedicalRequestModalVisibility(false)}>
                    <RequestMedicalLeave
                        close={() => setMedicalRequestModalVisibility(false)}
                        createMedicalLeaveRequestOnSuccess={createMedicalLeaveRequestOnSuccess}
                        isCreate={true}
                    />
                </Modal>
            )}

            <Modal isVisible={isRejectDetailsModalVisible} closeModal={() => setRejectDetailsModalVisibility(false)}>
                <MessageBox
                    description={rejectReason ?? ''}
                    title={t('leaveRequest.rejectionReason')}
                    close={() => setRejectDetailsModalVisibility(false)}
                />
            </Modal>
            <Modal isVisible={isDeleteDORModalVisible} closeModal={() => setDeleteDORModalVisible(false)}>
                <YesNo
                    title={t('common.areYouSure')}
                    description={t('leaveRequest.deleteInfo')}
                    onNo={() => setDeleteDORModalVisible(false)}
                    onYes={() => {
                        setDeleteDORModalVisible(false);
                        deleteLeaveRequestCb(leaveRequestToDeleteId.current);
                    }}
                />
            </Modal>
            <Modal isVisible={isTermsOpen} closeModal={() => setIsTermsOpen(false)}>
                <MessageBox description={policyDescription} title={t('leaveRequest.termsOfPolicy')} close={() => setIsTermsOpen(false)} />
            </Modal>
        </main>
    );
};

export default LeaveRequest;
